import { useState, useEffect } from 'react';
import Head from 'next/head';
import '../styles/globals.css';
import { SessionProvider } from 'next-auth/react';
import { AuthProvider } from '../contexts/AuthContext';
import { BetSlipProvider, useBetSlip } from '../contexts/BetSlipContext';
import { UserProfilesProvider } from '../contexts/UserProfilesContext';
import { ProfileCacheProvider } from '../contexts/ProfileCacheContext';
import { UserPreferencesProvider } from '../contexts/UserPreferencesContext';
import { GamesProvider } from '../contexts/GamesContext';
import { MatchupProvider, useMatchup } from '../contexts/MatchupContext';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import { PushNotificationsProvider } from '../contexts/PushNotificationsContext';
import { UserPreviewProvider } from '../contexts/UserPreviewContext';
import { SiteConfigProvider } from '../contexts/SiteConfigContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import GlobalToastContainer from '../components/notifications/GlobalToastContainer';
import AchievementUnlockOverlay from '../components/notifications/AchievementUnlockOverlay';
import IncomingInviteModal from '../components/battle/IncomingInviteModal';
import BonusClaimedCelebration from '../components/BonusClaimedCelebration';
import PushOptInPrompt from '../components/notifications/PushOptInPrompt';
import WonByForfeitModal from '../components/WonByForfeitModal';
import ChallengePopup from '../components/ChallengePopup';
import DemoPopup from '../components/DemoPopup';
import AuthPopup from '../components/AuthPopup';
import OnboardingPopup from '../components/OnboardingPopup';
import MyChallengePopup from '../components/MyChallengePopup';
import MobileNavMenu from '../components/MobileNavMenu';
import BetaLanding from '../components/BetaLanding';
import PublicBattlePreview from '../components/PublicBattlePreview';
import BetSlip from '../components/BetSlip';
import { useEventTracking } from '../hooks/useEventTracking';
import { releaseBodyScrollLock } from '../hooks/useGlobalScrollLockRecovery';
import { installTopNavClickTrapWatchdog } from '../utils/topNavClickTrapWatchdog';
import { useRouter } from 'next/router';
import App from 'next/app';
import {
  grantBetaAccess,
  setBetaAccessCookie,
  readBetaAccessFromCookieHeader,
} from '../utils/betaAccess';

function AnalyticsTracker() {
  const { trackPageView } = useEventTracking();
  const router = useRouter();

  useEffect(() => {
    trackPageView(router.pathname, document.title);

    const handleRouteChange = (url) => {
      trackPageView(url, document.title);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, trackPageView]);

  return null;
}

// Top-nav click-trap watchdog. Scoped to /messenger and /notifications
// — the two routes where the regression actually surfaces. An earlier
// revision installed this on every route to defend against a generic
// "orphan overlay" class of bug, but the watchdog's heuristic (any
// fixed-position element ≥90% × 90% of the viewport that isn't
// tagged `data-allow-fixed-overlay="true"` gets auto-removed every
// 1.5s) also matches LEGITIMATE UI: the mobile bet slip when expanded
// to fullscreen, walkthrough popups that mount over the dashboard,
// rush ready/voting overlays, etc. Users saw the bet slip and post-
// walkthrough popups flash on for a half-second and vanish — that was
// the watchdog destroying them mid-mount. We keep coverage on the
// two pages where the bug originated and let every other surface
// render its modals without interference.
function GlobalClickTrapWatchdog() {
  const router = useRouter();
  const pathname = router?.pathname || '';
  useEffect(() => {
    if (pathname !== '/messenger' && pathname !== '/notifications') return undefined;
    return installTopNavClickTrapWatchdog(pathname.slice(1));
  }, [pathname]);
  return null;
}

// Conservative, overlay-free scroll-lock recovery that runs on EVERY route.
// Distinct from GlobalClickTrapWatchdog above (scoped to /messenger +
// /notifications because its overlay-REMOVAL heuristic destroys legit
// fullscreen UI). This variant ONLY clears a stuck body scroll-lock and ONLY
// when no modal is open — it never detaches any element — so it's safe app-
// wide, including the battle flow. It rescues the case where a modal crashes
// mid-teardown and leaves `body { position: fixed; overflow: hidden }`,
// which otherwise freezes scrolling/taps until a full reload. The gate uses
// the same markers modals advertise (role=dialog/aria-modal, the scroll-lock
// owner attribute, and data-allow-fixed-overlay) so an open modal — or the
// bet slip, which never locks the body — is never disturbed.
function GlobalScrollLockRecovery() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const tick = () => {
      const b = document.body.style;
      const isLocked = b.position === 'fixed' || b.overflow === 'hidden';
      if (!isLocked) return;
      const hasOpenModal = !!document.querySelector(
        '[role="dialog"][aria-modal="true"], [data-scroll-lock-owner="true"], [data-allow-fixed-overlay="true"]'
      );
      if (!hasOpenModal) releaseBodyScrollLock('orphan body lock (global recovery)');
    };
    const interval = setInterval(tick, 1500);
    return () => clearInterval(interval);
  }, []);
  return null;
}

function ForfeitNoticeOverlay() {
  const { forfeitNotice, acknowledgeForfeit } = useMatchup();
  return (
    <WonByForfeitModal
      isOpen={!!forfeitNotice}
      onClose={acknowledgeForfeit}
      opponent={forfeitNotice?.opponent}
      payout={forfeitNotice?.winnerPayout}
    />
  );
}

function PresenceHeartbeat({ isLoggedIn }) {
  useEffect(() => {
    if (!isLoggedIn) return;

    let cancelled = false;
    const ping = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      try {
        await fetch('/api/user/heartbeat', { method: 'POST' });
      } catch (_e) {}
    };

    ping();
    const interval = setInterval(ping, 60000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') ping();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [isLoggedIn]);

  return null;
}

// Routes that intentionally hide the rest of the app chrome (top
// navbar, beta gate, etc.). Centralized here so the beta-auth gate and
// the GlobalBetSlip mount stay in lockstep — adding a new chrome-less
// route in one place automatically suppresses the slip there too,
// preventing an orphan panel on a screen that has no trigger button.
function isChromelessRoute(pathname) {
  const path = pathname || '';
  if (path.startsWith('/debug')) return true;
  if (path.startsWith('/admin')) return true;
  if (path === '/checkout' || path === '/checkout-design') return true;
  if (path === '/battle/replay/[id]') return true;
  return false;
}

// Wraps the active route so we can apply the desktop "PIK SLIP as side
// panel" shift in one place. When the slip is open AND the viewport is
// at the md breakpoint or larger, this wrapper translates the page
// content left by the panel width via the .betslip-open CSS class
// (defined in styles/globals.css). The transform is a no-op on mobile
// so the slip's existing full-screen behavior is preserved. Lives
// inside <BetSlipProvider> so it can read the context value directly,
// keeping individual pages free of per-page shift logic.
function PageShellShifter({ children }) {
  const { showBetSlip } = useBetSlip();
  return (
    <div
      className={`page-content-shift${showBetSlip ? ' betslip-open' : ''}`}
      style={{
        minHeight: '100vh',
        backgroundColor: '#000000',
        width: '100vw',
        position: 'relative',
      }}
    >
      {children}
    </div>
  );
}

// Single global Pik Slip mount. Lives inside <BetSlipProvider> so it
// reads the same `showBetSlip` flag the top navbar toggles, which means
// the slip opens on every page that shows the navbar — not only the
// pages that used to render their own <BetSlip /> instance. Suppressed
// on the same routes where the rest of the chrome is intentionally
// hidden today (debug, admin, checkout, replay), so we don't leak an
// orphan panel onto screens that don't show the trigger button. The
// beta-landing and public-battle-preview gates short-circuit above
// <BetSlipProvider> entirely, so they never reach this mount.
function GlobalBetSlip() {
  const router = useRouter();
  const { showBetSlip, setShowBetSlip } = useBetSlip();
  if (isChromelessRoute(router?.pathname)) return null;
  return (
    <BetSlip
      isOpen={showBetSlip}
      onClose={() => setShowBetSlip(false)}
    />
  );
}

function AutoGrader() {
  useEffect(() => {
    const gradeBets = async () => {
      try {
        const response = await fetch('/api/bets/grade', { method: 'POST' });
        if (response.ok) {
          const data = await response.json();
          if (data.graded > 0) {
            console.log(`[AutoGrader] Graded ${data.graded} bets`);
            window.dispatchEvent(new CustomEvent('betsGraded', { detail: data }));
          }
        }
      } catch (error) {
        console.error('[AutoGrader] Error:', error);
      }
    };

    gradeBets();

    const interval = setInterval(gradeBets, 60000);

    return () => clearInterval(interval);
  }, []);

  return null;
}

function MyApp({ Component, pageProps: { session, ...pageProps }, router, initialBetaAccess = false }) {
  // Defensive global cleanup: on every route change, clear any body/html
  // styles that modals (BetSlip, ActiveBattleCard, OnboardingPopup,
  // ChallengePopup, AuthPopup, BalanceModal, ShareableBetSlip, etc.) may
  // have set via useModalScrollLock or direct mutations. If a modal is
  // still open when a programmatic redirect, deep link, or service-worker
  // navigation fires, its cleanup may not run before the next page mounts,
  // leaving the body locked and swallowing taps. Mirrors task #158's
  // per-component fix for the mobile nav menu.
  useEffect(() => {
    if (!router?.events) return undefined;
    const release = () => releaseBodyScrollLock(null);
    router.events.on('routeChangeStart', release);
    router.events.on('routeChangeComplete', release);
    // Also clean up if a navigation is cancelled or fails — without this,
    // a back-button cancel mid-navigation could leave a stale lock in
    // place since neither Start nor Complete fires for aborted routes.
    router.events.on('routeChangeError', release);
    return () => {
      router.events.off('routeChangeStart', release);
      router.events.off('routeChangeComplete', release);
      router.events.off('routeChangeError', release);
    };
  }, [router]);

  const [showChallengePopup, setShowChallengePopup] = useState(false);
  const [selectedChallengeIndex, setSelectedChallengeIndex] = useState(1);
  const [showDemoPopup, setShowDemoPopup] = useState(false);
  const [showAuthPopup, setShowAuthPopup] = useState(false);
  const [authPopupMode, setAuthPopupMode] = useState('signin');
  const [showOnboardingPopup, setShowOnboardingPopup] = useState(false);
  const [showMyChallengePopup, setShowMyChallengePopup] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  // Initialize beta gate from the SSR-read cookie so the BetaLanding
  // component does not flash on hard reloads. On client-side navs the
  // state persists across pages because _app.js does not unmount.
  const [betaAuthenticated, setBetaAuthenticated] = useState(initialBetaAccess);
  const [justAuthenticated, setJustAuthenticated] = useState(false);

  // Theme state is now owned by `ThemeContext` (mounted below) — it
  // hydrates from `localStorage` (`piks-theme`) and a no-FOUC inline
  // script in `pages/_document.js` applies the right class before
  // first paint. No legacy clearing needed here.

  // Dev-only kill-switch for stale service workers. PWA is disabled in
  // development (next.config.js: `disable: NODE_ENV === 'development'`),
  // but a SW that was previously installed by a production build (or an
  // earlier dev session before PWA was disabled) keeps living in the
  // browser and intercepts requests with cached HTML — which then
  // mismatches the freshly-compiled dev JS bundle and surfaces as a
  // React hydration error ("Text content does not match server-rendered
  // HTML"). Auto-unregister any existing SW + nuke its caches on mount
  // in dev so users don't have to dig through DevTools to recover.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    let cancelled = false;
    (async () => {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        if (cancelled || regs.length === 0) return;
        await Promise.all(regs.map(r => r.unregister().catch(() => {})));
        if (typeof caches !== 'undefined') {
          try {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
          } catch (_e) {}
        }
        // One reload to drop any in-flight cached HTML and pick up the
        // fresh dev bundle. Guarded so we don't reload-loop if the SW
        // somehow re-registers — sessionStorage flag clears on tab close.
        try {
          if (!sessionStorage.getItem('piks_sw_killed')) {
            sessionStorage.setItem('piks_sw_killed', '1');
            window.location.reload();
          }
        } catch (_e) {}
      } catch (_e) {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Preload logo image on app mount
  useEffect(() => {
    const img = new Image();
    img.src = '/pikslogotransparent.png';
  }, []);

  // Scroll to top ONLY when user just authenticated (not on page reload/return)
  useEffect(() => {
    if (justAuthenticated) {
      // Force scroll to top multiple times to ensure it works
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      });
      
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 50);
      
      setTimeout(() => {
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 150);
      
      // Reset flag after scrolling
      setJustAuthenticated(false);
    }
  }, [justAuthenticated]);

  useEffect(() => {
    if (isLoggedIn && typeof window !== 'undefined') {
      const pendingLogin = localStorage.getItem('betslip_pending_login');
      if (pendingLogin) {
        try {
          const data = JSON.parse(pendingLogin);
          if (data.redirect === 'betslip' && Date.now() - data.timestamp < 300000) {
            localStorage.removeItem('betslip_pending_login');
            window.dispatchEvent(new CustomEvent('openBetSlip'));
          } else {
            localStorage.removeItem('betslip_pending_login');
          }
        } catch (e) {
          localStorage.removeItem('betslip_pending_login');
        }
      }
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const betaAccess = localStorage.getItem('beta_access');
      if (betaAccess === 'true') {
        setBetaAuthenticated(true);
        // Migrate existing users who already have localStorage but no
        // cookie yet — write the cookie so future hard reloads can SSR
        // the actual app instead of the gate.
        if (!initialBetaAccess) {
          setBetaAccessCookie();
        }
      }
    }

    const fetchUser = async () => {
      // Check NextAuth session first (passed as prop from SessionProvider)
      if (session?.user) {
        setCurrentUser(session.user);
        setIsLoggedIn(true);
        return;
      }
      
      // Fallback to localStorage for demo/local users
      const storedUser = localStorage.getItem('current_user');
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser && parsedUser.id) {
            setCurrentUser(parsedUser);
            setIsLoggedIn(true);
          }
        } catch (error) {
          localStorage.removeItem('current_user');
        }
      } else {
        // No session and no stored user - ensure logged out state
        setCurrentUser(null);
        setIsLoggedIn(false);
      }
    };

    fetchUser();

    const handleOpenChallengePopup = (e) => {
      if (e.detail && typeof e.detail.challengeIndex === 'number') {
        setSelectedChallengeIndex(e.detail.challengeIndex);
      }
      setShowChallengePopup(true);
    };

    const handleMobileMenuToggle = (e) => {
      setMobileMenuOpen(e.detail.isOpen);
    };

    const handleOpenDemoPopup = () => {
      setShowDemoPopup(true);
    };

    const handleOpenAuthPopup = (e) => {
      if (e.detail && e.detail.mode) {
        setAuthPopupMode(e.detail.mode);
      } else {
        setAuthPopupMode('signin');
      }
      setShowAuthPopup(true);
    };

    const handleUserLoggedOut = () => {
      setCurrentUser(null);
      setIsLoggedIn(false);
    };

    const handleOpenMyChallengePopup = () => {
      setShowMyChallengePopup(true);
    };

    const handleOpenOnboardingPopup = () => {
      setShowOnboardingPopup(true);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('openChallengePopup', handleOpenChallengePopup);
      window.addEventListener('mobileMenuToggle', handleMobileMenuToggle);
      window.addEventListener('openDemoPopup', handleOpenDemoPopup);
      window.addEventListener('openAuthPopup', handleOpenAuthPopup);
      window.addEventListener('userLoggedOut', handleUserLoggedOut);
      window.addEventListener('openMyChallengePopup', handleOpenMyChallengePopup);
      window.addEventListener('openOnboardingPopup', handleOpenOnboardingPopup);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('openChallengePopup', handleOpenChallengePopup);
        window.removeEventListener('mobileMenuToggle', handleMobileMenuToggle);
        window.removeEventListener('openDemoPopup', handleOpenDemoPopup);
        window.removeEventListener('openAuthPopup', handleOpenAuthPopup);
        window.removeEventListener('userLoggedOut', handleUserLoggedOut);
        window.removeEventListener('openMyChallengePopup', handleOpenMyChallengePopup);
        window.removeEventListener('openOnboardingPopup', handleOpenOnboardingPopup);
      }
    };
  }, [session]);

  // Individual flags are still used by chrome-specific gates below
  // (each route hides a slightly different combination of UI), but they
  // all derive from the same isChromelessRoute() helper that the
  // GlobalBetSlip mount uses, so adding a new chrome-less route in one
  // place keeps every gate in sync automatically.
  const isDebugPage = router?.pathname?.startsWith('/debug');
  const isAdminPage = router?.pathname?.startsWith('/admin');
  const isCheckoutPage = router?.pathname === '/checkout' || router?.pathname === '/checkout-design';
  const isReplayPage = router?.pathname === '/battle/replay/[id]';
  
  const battlePreview = pageProps?.battlePreview;
  const battlePreviewMeta = battlePreview ? (() => {
    const u1 = battlePreview.user1?.username || 'Player 1';
    const u2 = battlePreview.user2?.username || 'Opponent';
    const moment = battlePreview.moment || null;
    const momentId = battlePreview.momentId || moment?.id || null;
    let defaultTitle;
    if (battlePreview.winnerName) {
      defaultTitle = `${battlePreview.winnerName} won · ${u1} vs ${u2} on Piks`;
    } else if (battlePreview.isTie) {
      defaultTitle = `${u1} vs ${u2} ended in a tie on Piks`;
    } else {
      defaultTitle = `${u1} vs ${u2} on Piks`;
    }
    const descParts = [`${battlePreview.mode} battle`];
    if (battlePreview.scoreText) descParts.push(`Final ${battlePreview.scoreText}`);
    descParts.push(`${battlePreview.prize} prize pool`);
    descParts.push(battlePreview.statusLabel);
    if (moment?.selection) {
      const owner = moment.ownerUsername ? `${moment.ownerUsername}: ` : '';
      descParts.push(`Highlight · ${owner}${moment.selection}`);
    }
    const defaultDescription = `${descParts.join(' · ')}.`;
    const origin = battlePreview.origin || '';
    const momentQS = momentId ? `&m=${encodeURIComponent(momentId)}` : '';
    const sharePath = battlePreview.sharePath
      || `/bet-history?battle=${encodeURIComponent(battlePreview.matchupId)}${momentQS}`;
    const defaultUrl = `${origin}${sharePath}`;
    const title = battlePreview.title || defaultTitle;
    const description = battlePreview.description || defaultDescription;
    const imageQS = momentId ? `?m=${encodeURIComponent(momentId)}` : '';
    const image = battlePreview.image
      || `${origin}/api/og/battle/${encodeURIComponent(battlePreview.matchupId)}${imageQS}`;
    const url = battlePreview.url
      ? (battlePreview.url.startsWith('http') ? battlePreview.url : `${origin}${battlePreview.url}`)
      : defaultUrl;
    return { title, description, image, url };
  })() : null;

  const profilePreview = pageProps?.profilePreview;
  const profilePreviewMeta = profilePreview ? (() => {
    const username = profilePreview.username || 'Player';
    const origin = profilePreview.origin || '';
    const badge = profilePreview.badge;

    // When the share URL carries ?badge=<id>, the unlocked badge becomes
    // the hero of the unfurl: badge OG image, badge-specific title and
    // description, and the deep link points back at /profile/<id>?badge=<id>
    // so the destination page can highlight the same badge.
    if (badge && badge.achievementId) {
      const badgeId = badge.achievementId;
      const badgeName = badge.name || 'Achievement';
      const badgeRarity = badge.rarity || 'Common';
      const title = `@${username} unlocked the ${badgeName} ${badgeRarity} badge on Piks`;
      const description = `See the ${badgeName} ${badgeRarity} badge @${username} just earned on Piks — and chase yours.`;
      // Mirror the share URL produced by AchievementDetailModal so click
      // throughs from the unfurl carry the badge_share ref/b params used by
      // lib/badgeShareTracking.js as well as the ?badge= deep link param.
      const sharePath = `/profile/${encodeURIComponent(profilePreview.profileId)}?ref=badge_share&b=${encodeURIComponent(badgeId)}&badge=${encodeURIComponent(badgeId)}`;
      const url = `${origin}${sharePath}`;
      const image = `${origin}/api/og/badge/${encodeURIComponent(badgeId)}?u=${encodeURIComponent(username)}`;
      return { title, description, image, url };
    }

    const wins = profilePreview.wins ?? 0;
    const losses = profilePreview.losses ?? 0;
    const winRate = profilePreview.winRate ?? 0;
    const earnings = profilePreview.totalWinningsFormatted || '0';
    const defaultTitle = `@${username} on Piks`;
    const descParts = [`${wins}W–${losses}L`];
    if (winRate) descParts.push(`${winRate}% win rate`);
    descParts.push(`${earnings} coins earned`);
    const defaultDescription = `${descParts.join(' · ')}.`;
    const sharePath = `/profile/${encodeURIComponent(profilePreview.profileId)}`;
    const url = `${origin}${sharePath}`;
    const image = `${origin}/api/og/profile/${encodeURIComponent(profilePreview.profileId)}`;
    return { title: defaultTitle, description: defaultDescription, image, url };
  })() : null;

  const profilePreviewHead = profilePreviewMeta ? (
    <Head>
      <title>{profilePreviewMeta.title}</title>
      <meta name="description" content={profilePreviewMeta.description} />
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={profilePreviewMeta.title} />
      <meta property="og:description" content={profilePreviewMeta.description} />
      <meta property="og:image" content={profilePreviewMeta.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={profilePreviewMeta.url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={profilePreviewMeta.title} />
      <meta name="twitter:description" content={profilePreviewMeta.description} />
      <meta name="twitter:image" content={profilePreviewMeta.image} />
    </Head>
  ) : null;

  const battlePreviewHead = battlePreviewMeta ? (
    <Head>
      <title>{battlePreviewMeta.title}</title>
      <meta name="description" content={battlePreviewMeta.description} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={battlePreviewMeta.title} />
      <meta property="og:description" content={battlePreviewMeta.description} />
      <meta property="og:image" content={battlePreviewMeta.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={battlePreviewMeta.url} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={battlePreviewMeta.title} />
      <meta name="twitter:description" content={battlePreviewMeta.description} />
      <meta name="twitter:image" content={battlePreviewMeta.image} />
    </Head>
  ) : null;

  if (!betaAuthenticated && !isDebugPage && !isAdminPage && !isCheckoutPage && !isReplayPage) {
    if (battlePreview) {
      return (
        <>
          {battlePreviewHead}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              zIndex: -1,
              backgroundColor: '#000000',
            }}
          />
          <PublicBattlePreview
            preview={battlePreview}
            onJoinClick={() => {
              grantBetaAccess();
              if (typeof window !== 'undefined') {
                window.__pendingBattleOpen = battlePreview.matchupId;
              }
              setBetaAuthenticated(true);
              setJustAuthenticated(true);
              setAuthPopupMode('signup');
              setShowAuthPopup(true);
            }}
            onLoginClick={() => {
              grantBetaAccess();
              if (typeof window !== 'undefined') {
                window.__pendingBattleOpen = battlePreview.matchupId;
              }
              setBetaAuthenticated(true);
              setJustAuthenticated(true);
              setAuthPopupMode('signin');
              setShowAuthPopup(true);
            }}
          />
        </>
      );
    }
    return (
      <>
        {battlePreviewHead}
        {profilePreviewHead}
        {/* Solid Black Background */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            zIndex: -1,
            backgroundColor: '#000000',
          }}
        />
        <BetaLanding onAuthenticated={() => { setBetaAuthenticated(true); setJustAuthenticated(true); }} />
      </>
    );
  }

  return (
    <SessionProvider session={session}>
      {battlePreviewHead}
      {profilePreviewHead}
      <SiteConfigProvider>
      <ThemeProvider>
      <AuthProvider>
          <UserPreferencesProvider>
          <BetSlipProvider>
            <UserProfilesProvider>
              <ProfileCacheProvider>
              <GamesProvider initialInplayEvents={pageProps.initialInplayEvents} initialApiGames={pageProps.initialApiGames}>
                <MatchupProvider>
                <NotificationsProvider>
                <PushNotificationsProvider>
                <UserPreviewProvider>
                <ForfeitNoticeOverlay />
                <AnalyticsTracker />
                <GlobalClickTrapWatchdog />
                <GlobalScrollLockRecovery />
                <PresenceHeartbeat isLoggedIn={isLoggedIn} />
                <AutoGrader />
                <GlobalToastContainer />
                <AchievementUnlockOverlay />
                <IncomingInviteModal />
                <PushOptInPrompt />
                <BonusClaimedCelebration />
                <GlobalBetSlip />
                {/* Solid Black Background */}
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100vh',
                    zIndex: -1,
                    backgroundColor: '#000000',
                  }}
                />
                <style jsx global>{`
                  body {
                    overflow-x: hidden;
                  }
                `}</style>

                {/* Logo preloader - loads once, stays cached for all pages */}
                <img 
                  src="/pikslogotransparent.png" 
                  alt="" 
                  aria-hidden="true"
                  style={{
                    position: 'fixed',
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: 'none',
                    zIndex: -1
                  }}
                />
                
                {/* Page wrapper - menu opens instantly with no slide animation.
                    On desktop (md+), this wrapper translates left by the
                    bet-slip panel width (420px) when the slip is open, so the
                    docked side panel reveals on the right and both are visible
                    side-by-side. Mobile is unchanged — the slip continues to
                    take over the full viewport. The shift is handled by
                    PageShellShifter so it can read showBetSlip from context. */}
                <PageShellShifter>
                  <Component {...pageProps} />
                </PageShellShifter>
                
                {/* Global Popups - Available on all pages */}
                <ChallengePopup 
                  isOpen={showChallengePopup} 
                  onClose={() => setShowChallengePopup(false)}
                  initialIndex={selectedChallengeIndex}
                />
                
                <DemoPopup 
                  isOpen={showDemoPopup} 
                  onClose={() => setShowDemoPopup(false)} 
                />

                <AuthPopup 
                  isOpen={showAuthPopup} 
                  onClose={() => setShowAuthPopup(false)}
                  initialMode={authPopupMode}
                />

                <OnboardingPopup
                  isOpen={showOnboardingPopup}
                  onClose={() => setShowOnboardingPopup(false)}
                />

                <MyChallengePopup
                  isOpen={showMyChallengePopup}
                  onClose={() => setShowMyChallengePopup(false)}
                />

                {/* Mobile Menu - Rendered outside page wrapper via portal */}
                <MobileNavMenu
                  isOpen={mobileMenuOpen}
                  onClose={() => {
                    setMobileMenuOpen(false);
                    window.dispatchEvent(new CustomEvent('mobileMenuClosed'));
                  }}
                  currentUser={currentUser}
                  isLoggedIn={isLoggedIn}
                />
                </UserPreviewProvider>
                </PushNotificationsProvider>
                </NotificationsProvider>
                </MatchupProvider>
              </GamesProvider>
              </ProfileCacheProvider>
            </UserProfilesProvider>
          </BetSlipProvider>
          </UserPreferencesProvider>
        </AuthProvider>
      </ThemeProvider>
      </SiteConfigProvider>
    </SessionProvider>
  );
}

// Read the beta_access cookie server-side so the BetaLanding gate does
// not flash on hard reloads. NOTE: defining MyApp.getInitialProps
// disables Next.js Automatic Static Optimization globally — every page
// renders per-request, including content pages (terms, privacy,
// responsible-pikking) that would otherwise be statically
// served. We accept this trade-off because the gate flash is the more
// visible UX problem during private beta and most pages in this app
// already use getServerSideProps for live data anyway. If/when the gate
// is removed (or moved into middleware), delete this hook to restore
// Automatic Static Optimization on the remaining content pages.
MyApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  const cookieHeader = appContext?.ctx?.req?.headers?.cookie || '';
  const initialBetaAccess = readBetaAccessFromCookieHeader(cookieHeader);
  return { ...appProps, initialBetaAccess };
};

export default MyApp;